import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Form, Input, message, Modal, Space, Spin, Table, Typography, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, WifiOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { getWifiList, createWifi, updateWifi, deleteWifi } from 'src/features/wifi/wifi.api';
import { CompanyWifi, CompanyWifiCreate, CompanyWifiUpdate } from 'src/features/wifi/wifi.types';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';
import { CompanyWifiDeleteResponse } from 'src/features/wifi/wifi.types';

const CompanyWifiTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [wifiList, setWifiList] = useState<CompanyWifi[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWifi, setEditingWifi] = useState<CompanyWifi | null>(null);

  const fetchWifiList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWifiList({ companyId });
      if (response.status === 200) {
        const transformedWifi = response.data.wifi.map((item: any) => ({
          id: item.id,
          company_id: item.companyId,
          ssid: item.ssid,
          password: item.password,
          location_description: item.locationDescription,
        }));
        setWifiList(transformedWifi);
      } else {
        message.error('Ошибка загрузки WiFi сетей');
      }
    } catch (error) {
      console.error('Failed to fetch WiFi list:', error);
      message.error('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchWifiList();
  }, [fetchWifiList]);

  const handleAdd = () => {
    setEditingWifi(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: CompanyWifi) => {
    setEditingWifi(record);
    form.setFieldsValue({
      ssid: record.ssid,
      password: record.password,
      location_description: record.location_description,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = (await deleteWifi({ companyId, wifiId: id })) as {
        status: number;
        data: CompanyWifiDeleteResponse;
      };
      if (response.status === 200 && response.data) {
        message.success('WiFi сеть удалена');
        fetchWifiList();
      } else {
        message.error('WiFi сеть не найдена');
      }
    } catch (error) {
      console.error('Failed to delete WiFi:', error);
      message.error('Ошибка удаления');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingWifi) {
        const updateData: CompanyWifiUpdate = {
          id: editingWifi.id,
          ssid: values.ssid,
          password: values.password || null,
          location_description: values.location_description || null,
        };
        const response = await updateWifi({ companyId, data: updateData });
        if (response.status === 200) {
          message.success('WiFi сеть обновлена');
          setModalVisible(false);
          fetchWifiList();
        }
      } else {
        const createData: CompanyWifiCreate = {
          ssid: values.ssid,
          password: values.password || null,
          location_description: values.location_description || null,
        };
        const response = await createWifi({ companyId, data: createData });
        if (response.status === 201) {
          message.success('WiFi сеть создана');
          setModalVisible(false);
          fetchWifiList();
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      message.error('Ошибка сохранения');
    }
  };

  const truncateText = (text: string | null, maxLength = 10) => {
    if (!text) return text;
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  const columns: ColumnsType<CompanyWifi> = [
    {
      title: 'Имя сети (SSID)',
      dataIndex: 'ssid',
      key: 'ssid',
      render: (text: string) => {
        const truncated = truncateText(text, 10);
        if (truncated !== text) {
          return (
            <Tooltip title={text}>
              <strong>{truncated}</strong>
            </Tooltip>
          );
        }
        return <strong>{text}</strong>;
      },
    },
    {
      title: 'Пароль',
      dataIndex: 'password',
      key: 'password',
      render: (text: string | null) => {
        if (!text) return <span style={{ color: '#999' }}>Нет пароля</span>;

        const truncated = truncateText(text, 10);
        if (truncated !== text) {
          return (
            <Tooltip title={text}>
              <span>{truncated}</span>
            </Tooltip>
          );
        }
        return <span>{text}</span>;
      },
    },
    {
      title: 'Описание',
      dataIndex: 'location_description',
      key: 'location_description',
      render: (text: string | null) => {
        if (!text) return <span style={{ color: '#999' }}>Не указано</span>;

        const truncated = truncateText(text, 10);
        if (truncated !== text) {
          return (
            <Tooltip title={text}>
              <span>{truncated}</span>
            </Tooltip>
          );
        }
        return <span>{text}</span>;
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size='small' icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Редактировать
          </Button>
          <Popconfirm title='Удалить WiFi сеть?' onConfirm={() => handleDelete(record.id)} okText='Да' cancelText='Нет'>
            <Button size='small' danger icon={<DeleteOutlined />}>
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4}>
          <WifiOutlined style={{ marginRight: 8 }} />
          WiFi сети
        </Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Добавить WiFi
          </Button>
        </ComponentRoleProtected>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={wifiList}
          rowKey='id'
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Нет WiFi сетей' }}
        />
      </Spin>

      <Modal
        title={editingWifi ? 'Редактировать WiFi сеть' : 'Добавить WiFi сеть'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        okText='Сохранить'
        cancelText='Отмена'
        width={500}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='ssid'
            label='Имя сети (SSID)'
            rules={[
              { required: true, message: 'Введите название WiFi сети' },
              { max: 64, message: 'Название не более 64 символов' },
            ]}
          >
            <Input placeholder='Например: Hotel WiFi' />
          </Form.Item>

          <Form.Item name='password' label='Пароль' rules={[{ max: 64, message: 'Пароль не более 64 символов' }]}>
            <Input.Password placeholder='Оставьте пустым для открытой сети' />
          </Form.Item>

          <Form.Item
            name='location_description'
            label='Описание'
            rules={[{ max: 500, message: 'Описание не более 500 символов' }]}
          >
            <Input.TextArea rows={3} placeholder='Например: Главный холл, ресепшн, конференц-зал' />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CompanyWifiTab;
